import { useState, useEffect } from "react";
import DialogModal from "../../../atoms/dialog";
import { RowData, SetSelect } from "../types";
import DownloadInvoiceTable from "./DownloadInvoiceTable";
import MismatchedAddressEditor from "./MismatchedAddressEditor";

type DownloadInvoicePdfDialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  rowsData: RowData[];
  setSelect: SetSelect;
}

function DownloadInvoicePdfDialog({ 
  isOpen, 
  handleClose, 
  rowsData: initialRowsData, 
  setSelect 
}: DownloadInvoicePdfDialogProps) {
  // Create a state for rowsData so we can update it
  const [transactionData, setTransactionData] = useState<RowData[]>(initialRowsData);
  const [showAddressEditor, setShowAddressEditor] = useState(true);
  
  // Identify transactions with mismatched addresses
  const mismatchedTransactions = transactionData.filter((transaction: any) => {
    const countryCode = transaction.bankAccountValue?.country?.toLowerCase() || "";
    const addressText = (
      (transaction.bankAccountValue?.bank_details?.account_holder_address || "") +
      (transaction.bankAccountValue?.bank_details?.account_holder_residing_country || "")
    ).toLowerCase();

    return !addressText.includes(countryCode);
  });

  // Reset component state when dialog opens with new data
  useEffect(() => {
    if (isOpen) {
      setTransactionData(initialRowsData);
      setShowAddressEditor(true);
    }
  }, [isOpen, initialRowsData]);

  // Check if addresses need to be fixed
  const hasAddressMismatches = mismatchedTransactions.length > 0;

  // Handle address updates
  const handleAddressUpdate = (updatedTransactions: RowData[]) => {
    // Create a map of updated transactions by callId for easy lookup
    const updatedTransactionsMap = updatedTransactions.reduce((acc, tx) => {
      if (tx.invoice_no) {
        acc[tx.invoice_no] = tx;
      }
      return acc;
    }, {} as Record<string, RowData>);
    
    // Update the full transaction data with the corrected addresses
    const newTransactionData = transactionData.map(tx => {
      if (tx.invoice_no && updatedTransactionsMap[tx.invoice_no]) {
        return updatedTransactionsMap[tx.invoice_no];
      }
      return tx;
    });
    
    setTransactionData(newTransactionData);
    setShowAddressEditor(false);
  };
  

  return (
    <DialogModal
      isOpen={isOpen}
      handleClose={handleClose}
      title="Download Invoice PDF"
    >
      {hasAddressMismatches && showAddressEditor ? (
        <MismatchedAddressEditor
          mismatchedTransactions={mismatchedTransactions}
          onCancel={handleClose}
          onUpdate={handleAddressUpdate}
        />
      ) : (
        <DownloadInvoiceTable 
          rowsData={transactionData} 
          setSelect={setSelect} 
        />
      )}
    </DialogModal>
  );
}

export default DownloadInvoicePdfDialog;